/**
 * Task Card Component
 * 
 * Displays a task card within a Kanban column.
 * Shows task title, assignee, due date, priority, tags, and other metadata.
 * Supports drag-and-drop functionality.
 */

import React, { useState } from 'react';
import { Badge, Modal, Button } from 'react-bootstrap';
import { TaskCard } from '../../types';
import './TaskCardComponent.css';

interface TaskCardComponentProps {
  task: TaskCard;
  onClick: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

/**
 * Individual task card component
 */
const TaskCardComponent: React.FC<TaskCardComponentProps> = ({
  task,
  onClick,
  onDragStart,
  onDragEnd,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);
  /**
   * Get priority badge variant based on priority level
   */
  const getPriorityVariant = (priority?: string): string => {
    switch (priority) {
      case 'urgent':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'secondary';
      default:
        return 'light';
    }
  };

  /**
   * Get priority icon
   */
  const getPriorityIcon = (priority?: string): string => {
    switch (priority) {
      case 'urgent':
        return 'bi-exclamation-triangle-fill';
      case 'high':
        return 'bi-arrow-up-circle-fill';
      case 'medium':
        return 'bi-dash-circle-fill';
      case 'low':
        return 'bi-arrow-down-circle-fill';
      default:
        return '';
    }
  };

  /**
   * Format due date display
   */
  const formatDueDate = (date: Date): string => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) {
      return `Overdue ${Math.abs(days)}d`;
    } else if (days === 0) {
      return 'Due today';
    } else if (days === 1) {
      return 'Due tomorrow';
    } else if (days <= 7) {
      return `Due in ${days}d`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  /**
   * Check if due date is overdue or upcoming
   */
  const getDueDateStatus = (date: Date): 'overdue' | 'soon' | 'normal' => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'overdue';
    if (days <= 2) return 'soon';
    return 'normal';
  };

  /**
   * Get initials from assignee email or name
   */
  const getInitials = (email: string): string => {
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  /**
   * Get assignee display name from email
   */
  const getAssigneeName = (email: string): string => {
    const namePart = email.split('@')[0];
    return namePart.charAt(0).toUpperCase() + namePart.slice(1).replace(/[._-]/g, ' ');
  };

  const dueDateStatus = task.dueDate ? getDueDateStatus(task.dueDate) : null;

  return (
    <div
      className="task-card"
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      {/* Task Title */}
      <div className="task-card-title" title={task.title}>
        {task.title}
      </div>

      {/* Task Description Preview */}
      {task.description && (
        <div className="task-card-description">
          {task.description.substring(0, 100)}
          {task.description.length > 100 && '...'}
        </div>
      )}

      {/* Task Image Preview */}
      {task.imageUrl && (
        <div className="task-image-preview" style={{ marginBottom: '12px' }}>
          <img
            src={task.imageUrl}
            alt="Task related"
            onClick={(e) => {
              e.stopPropagation();
              setShowImageModal(true);
            }}
            style={{
              width: '100%',
              maxHeight: '120px',
              objectFit: 'cover',
              borderRadius: '6px',
              cursor: 'pointer',
              border: '1px solid #e9ecef'
            }}
          />
        </div>
      )}

      {/* Task Metadata */}
      <div className="task-card-metadata">
        {/* Priority Badge */}
        {task.priority && (
          <Badge
            bg={getPriorityVariant(task.priority)}
            className="task-priority-badge"
          >
            <i className={`bi ${getPriorityIcon(task.priority)} me-1`}></i>
            {task.priority}
          </Badge>
        )}

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
          <div className="task-tags">
            {task.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} bg="secondary" className="task-tag">
                {tag}
              </Badge>
            ))}
            {task.tags.length > 3 && (
              <Badge bg="light" text="dark" className="task-tag">
                +{task.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Task Footer */}
      <div className="task-card-footer">
        {/* Due Date */}
        {task.dueDate && (
          <div className={`task-due-date ${dueDateStatus}`}>
            <i className="bi bi-calendar-event me-1"></i>
            {formatDueDate(task.dueDate)}
          </div>
        )}

        {/* Right Side Footer Items */}
        <div className="task-card-footer-right">
          {/* Comment Count */}
          {task.commentCount && task.commentCount > 0 && (
            <div className="task-comment-count" title={`${task.commentCount} comments`}>
              <i className="bi bi-chat-left-text"></i>
              <span>{task.commentCount}</span>
            </div>
          )}

          {/* Attachment Count */}
          {task.attachments && task.attachments.length > 0 && (
            <div className="task-attachment-count" title={`${task.attachments.length} attachments`}>
              <i className="bi bi-paperclip"></i>
              <span>{task.attachments.length}</span>
            </div>
          )}

          {/* Assignee Avatar */}
          {task.assignee && (
            <div className="task-assignee-container">
              <div className="task-assignee-avatar" title={task.assignee}>
                {getInitials(task.assignee)}
              </div>
              <span className="task-assignee-name">
                {getAssigneeName(task.assignee)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal for larger view */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Task Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {task.imageUrl && (
            <img
              src={task.imageUrl}
              alt="Task related - full size"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
          {task.imageUrl && (
            <Button
              variant="primary"
              onClick={() => window.open(task.imageUrl, '_blank')}
            >
              <i className="bi bi-arrow-up-right-square me-2"></i>
              Open Full Size
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskCardComponent;

